import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import type { PreferencesResponse } from '../../../../@types/preferences';
import { makeAPIRequestReactQuery } from '../../../../react-query/api';
import { endpoints } from '../../../../react-query/endpoints';
import { useClaimVisit } from '../../../insuranceExport/hooks/useClaimVisit';
import type { TClaimFindingsResponse } from '../../../insuranceExport/types';
import type { TView } from '../state';

export type TSupportedTreatmentType = 'SRP' | null;

const useImages = ({
  estimatedVisitId,
  unifiedAppointmentId,
  organizationId,
  practiceId,
  onSuccess,
}: {
  estimatedVisitId: number;
  unifiedAppointmentId?: number;
  organizationId: string;
  practiceId: string;
  onSuccess?: (data: TClaimFindingsResponse) => void;
}) => {
  const path = `/v4/${endpoints.claimFindings}`;

  return useQuery(
    [path, estimatedVisitId, unifiedAppointmentId],
    async () => {
      const resp = await makeAPIRequestReactQuery<TClaimFindingsResponse>(
        path,
        {},
        'POST',
        {
          data: {
            estimatedVisitId: Number(estimatedVisitId),
            unifiedAppointmentId,
          },
          headers: {
            Org: organizationId,
            Practice: practiceId,
          },
        },
      );
      onSuccess?.(resp);
      return resp;
    },
    {
      enabled: !!estimatedVisitId && !!unifiedAppointmentId,
    },
  );
};

export const useSummaryImages = ({
  unifiedAppointmentId,
  defaultEstimatedVisitId,
  defaultImagingPatientId,
  appointmentImagingPatientId,
  appointmentStart,
}: {
  unifiedAppointmentId?: number;
  defaultEstimatedVisitId?: string;
  defaultImagingPatientId?: string;
  appointmentImagingPatientId?: string;
  appointmentStart?: string;
  preferences: PreferencesResponse;
}) => {
  const {
    estimatedVisitId: customizeSummaryVisitId,
    isLoading: isLoadingCustomizeSummary,
    visits,
  } = useClaimVisit({
    imagingPatientId: appointmentImagingPatientId || defaultImagingPatientId,
    appointmentOn: appointmentStart,
  });

  const visitData = visits.find((v) => v.id === customizeSummaryVisitId);
  const estimatedVisitId = defaultEstimatedVisitId
    ? Number(defaultEstimatedVisitId)
    : Number(visitData?.id);

  const { primaryPracticeId: practiceId, primaryOrganizationId: orgId } =
    useSelector((state: any) => state.login.user);

  const { data, isLoading } = useImages({
    estimatedVisitId,
    unifiedAppointmentId,
    organizationId: orgId,
    practiceId,
  });

  const sortedTreatments = data?.treatments?.sort((a, b) => {
    // First sort by isSupportedTreatmentGroup (supported treatments first)
    if (a.txGroupShortName !== b.txGroupShortName) {
      return (
        (b.txGroupShortName === 'srp' ? 1 : 0) -
        (a.txGroupShortName === 'srp' ? 1 : 0)
      );
    }
    // Then sort by txGroupShortName to group similar treatments
    if (a.txGroupShortName && b.txGroupShortName) {
      return a.txGroupShortName.localeCompare(b.txGroupShortName);
    }
    return 0;
  });

  return {
    treatments: sortedTreatments ?? [],
    claimImages: data?.claimImages,
    images: data?.images ?? [],
    supportedTreatmentType: data?.supportedTreatmentType ?? null,
    isLoading: isLoadingCustomizeSummary || isLoading,
    claimTeeth: data?.claimTeeth,
    claimNotes: data?.claimNotes,
    dmfValues: data?.dmfValues,
    defaultTeeth: data?.defaultTeeth,
    supportedTreatmentGroups: data?.supportedTreatmentGroups,
  };
};

export const useSummaryVisit = ({
  appointmentStart,
  appointmentImagingPatientId,
  selectedVisitId,
  selectedPatientId,
  insView,
}: {
  appointmentStart?: string;
  imagingPatientId?: string;
  estimatedVisitId?: number;
  selectedVisitId: string | null;
  selectedPatientId: string | null;
  appointmentImagingPatientId: string | null;
  insView: TView;
}) => {
  const {
    estimatedVisitId: customizeSummaryVisitId,
    imagingPatientId: customizeSummaryImagingPatientId,
    isLoading: isLoadingCustomizeSummary,
    visits,
  } = useClaimVisit({
    imagingPatientId: appointmentImagingPatientId || selectedPatientId || '',
    appointmentOn: appointmentStart,
  });

  const {
    estimatedVisitId,
    imagingPatientId,
    visitDate,
    radiographsTakenDate,
  } = useMemo(() => {
    // opening from customize button
    if (insView === 'appointment') {
      const selectedVisit = visits.find(
        (v) => v.id === customizeSummaryVisitId,
      );

      return {
        estimatedVisitId: customizeSummaryVisitId
          ? String(customizeSummaryVisitId)
          : null,
        imagingPatientId: customizeSummaryImagingPatientId || '',
        visitDate: appointmentStart || '',
        radiographsTakenDate: selectedVisit?.visitDate || null,
      };
    }

    // opening from image viewer sidebar
    return {
      estimatedVisitId: selectedVisitId,
      imagingPatientId: selectedPatientId || '',
      visitDate: visits.find((v) => v.id === Number(selectedVisitId))
        ?.visitDate,
      radiographsTakenDate: null,
    };
  }, [
    selectedVisitId,
    selectedPatientId,
    customizeSummaryVisitId,
    customizeSummaryImagingPatientId,
    appointmentStart,
    visits,
    insView,
  ]);

  return {
    estimatedVisitId,
    imagingPatientId,
    visitDate,
    radiographsTakenDate: radiographsTakenDate?.toString() ?? '',
    isLoading: isLoadingCustomizeSummary,
  };
};
